"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createLoanTapeEvaluator = void 0;
const lodash_1 = __importDefault(require("lodash"));
const common_1 = require("@finley-technologies/common");
const tape_sanitizer_1 = require("@finley-technologies/tape-sanitizer");
const intermediateCalcEvaluator_1 = require("./intermediateCalcEvaluator");
const progressHelper_1 = require("./progressHelper");
const createPostProcessedFields = ({ requiredIntermediateFields, orgSlugMap, loanTapeType, creditFacilityId, signaledExternalAssignment, baseFields, }) => {
    const { originalOwnerSlug } = requiredIntermediateFields;
    const organization = orgSlugMap[originalOwnerSlug];
    (0, common_1.finleyAssert)(organization, `organization must exist:[${originalOwnerSlug}]`);
    let preassignmentCreditFacilityId = null;
    switch (loanTapeType) {
        case 'masterLoanTape':
            // masterLoanTapeType is always null
            break;
        case 'preassignedLoanTape':
            preassignmentCreditFacilityId = creditFacilityId;
            break;
        case 'preassignedMasterLoanTape': {
            (0, common_1.finleyAssert)(signaledExternalAssignment, `signaledExternalAssignment configuration must exist for preassignedMasterLoanTape [creditFacilityId:${creditFacilityId}]`);
            const { signaledExternalAssignmentField, signaledExternalAssignment: signaledExternalAssignmentMatch, } = signaledExternalAssignment;
            const value = baseFields[signaledExternalAssignmentField];
            (0, common_1.finleyAssert)(!lodash_1.default.isNil(value), `signaledExternalAssignment must defined in preassignedMasterLoanTape for [creditFacilityId:${creditFacilityId}]`);
            if (String(value).match(signaledExternalAssignmentMatch)) {
                preassignmentCreditFacilityId = creditFacilityId;
            }
            break;
        }
        default:
            (0, common_1.finleyAssertUnreachable)(loanTapeType);
    }
    const postProcessedFields = {
        originalOwnerId: organization.id,
        normalizedExposure: Math.max(0, requiredIntermediateFields.exposure),
        preassignmentCreditFacilityId,
        performingExposure: requiredIntermediateFields.normalizedisChargedOff ||
            requiredIntermediateFields.normalizedisDelinquent
            ? 0
            : requiredIntermediateFields.exposure,
    };
    return postProcessedFields;
};
const createLoanTapeEvaluator = ({ config, loanTapeType, creditFacilityId, orgSlugMap, loanTapeMetadataExtractor, f1EngineMode, }) => __awaiter(void 0, void 0, void 0, function* () {
    const externalReceivableIdField = config._config_.externalReceivableIdField;
    const { baseFieldConfigs, signaledExternalAssignment } = config._config_;
    const { loanProductId } = config;
    const loanTapeMetadata = yield loanTapeMetadataExtractor();
    const intermediateCalcEvaluator = (0, intermediateCalcEvaluator_1.createIntermediateCalcEvaluator)(config._config_.intermediateCalcConfigs, f1EngineMode);
    const evaluateIntermediate = ({ baseFields, externalReceivableId, }) => {
        // intermediate calc evaluation
        const { requiredIntermediateFields, dynamicIntermediateFields, } = intermediateCalcEvaluator.evaluate({
            baseFields,
            loanTapeMetadata,
            externalReceivableId,
        });
        // postProcessedFields
        const postProcessedFields = createPostProcessedFields({
            requiredIntermediateFields,
            orgSlugMap,
            baseFields,
            loanTapeType,
            creditFacilityId,
            signaledExternalAssignment,
        });
        // return everything.
        return {
            requiredIntermediateFields,
            dynamicIntermediateFields,
            postProcessedFields,
        };
    };
    const sanitizedRowToBaseFields = ({ sanitizedRow, }) => {
        const baseFields = sanitizedRow;
        const externalReceivableId = sanitizedRow[externalReceivableIdField];
        (0, common_1.finleyAssert)(externalReceivableId !== null && externalReceivableId !== undefined, `Could not find externalReceivableId [${externalReceivableIdField}] for CSV row`);
        return {
            baseFields,
            externalReceivableId: String(externalReceivableId),
        };
    };
    const convertToCreditFacilityReceivables = ({ csv, progressCallback, assignmentFilter, }) => __awaiter(void 0, void 0, void 0, function* () {
        const sanitizedResults = yield (0, tape_sanitizer_1.sanitizeTape)({
            csv,
            baseFieldConfig: baseFieldConfigs,
            externalReferenceIdField: externalReceivableIdField,
            assignmentFilter,
        });
        if (!sanitizedResults.isValid) {
            return {
                errors: sanitizedResults.errors,
                creditFacilityReceivableWithoutFinleyId: [],
            };
        }
        const limitedTape = sanitizedResults.data;
        // we will report progress at every 4 seconds or after every 10% of rows - whichever happens first.
        const numRows = limitedTape.length;
        const { updateProgress } = (0, progressHelper_1.createProgressHelper)({
            progressCallback,
            operation: 'Convert to credit facility receivables',
            numRows,
        });
        const creditFacilityReceivableWithoutFinleyId = [];
        // eslint-disable-next-line no-restricted-syntax, no-plusplus
        for (let index = 0; index < limitedTape.length; index++) {
            const sanitizedRow = limitedTape[index];
            // eslint-disable-next-line no-await-in-loop
            yield updateProgress(index);
            //  run base fields and intermediate calcs.
            const { baseFields, externalReceivableId } = sanitizedRowToBaseFields({
                sanitizedRow,
            });
            //  run intermediate calcs.
            const { requiredIntermediateFields, dynamicIntermediateFields, postProcessedFields, } = evaluateIntermediate({
                baseFields,
                externalReceivableId,
            });
            creditFacilityReceivableWithoutFinleyId.push({
                sanitizedRow,
                loanProductId,
                externalReceivableId,
                baseFields,
                dynamicIntermediateFields,
                requiredIntermediateFields,
                postProcessedFields,
            });
        }
        return {
            creditFacilityReceivableWithoutFinleyId,
            errors: [],
        };
    });
    return {
        evaluateIntermediate,
        convertToCreditFacilityReceivables,
    };
});
exports.createLoanTapeEvaluator = createLoanTapeEvaluator;
//# sourceMappingURL=loanTapeEvaluator.js.map